import ApiWithLogging from '../utils/apiWithLogging';
import Logger from '../utils/logger';

// ประเภทข้อมูลผู้ใช้
export interface User {
  id: number;
  email: string;
  username: string;
  name?: string; // Added for Layout compatibility
  full_name?: string;
  phone?: string;
  is_active: boolean;
  is_superuser: boolean;
  is_superadmin?: boolean; // Added missing property
  role?: string; // Added for compatibility
  roles?: string[]; // Added for compatibility with AuthGuard
  role_id?: number;
  role_name?: string;
  created_at: string;
  updated_at?: string;
  last_login?: string;
}

// ข้อมูลสำหรับการสร้างหรือแก้ไขผู้ใช้
export interface UserForm {
  email: string;
  username: string;
  full_name?: string;
  phone?: string;
  password?: string;
  is_active?: boolean;
  is_superuser?: boolean;
  role_id?: number;
}

// พารามิเตอร์สำหรับการดึงข้อมูลผู้ใช้
export interface GetUsersParams {
  page?: number;
  limit?: number;
  search?: string;
  role_id?: number;
  is_active?: boolean;
  /** เฉพาะคนที่ login ได้จริง (บัญชีเปิด + บทบาทไม่ถูกปิด) — picker มอบหมายงาน (BUG-S2315-002) */
  can_sign_in?: boolean;
}

// การตอบกลับจากการดึงข้อมูลผู้ใช้
export interface GetUsersResponse {
  items: User[];
  total: number;
  page: number;
  limit: number;
}

// ข้อมูลผู้ดำเนินการสำหรับ dropdown
export interface PerformerOption {
  id: number;
  full_name: string | null;
  username: string;
}

// ฟังก์ชันสำหรับแปลงข้อมูลจาก API ให้เป็นโครงสร้างที่ต้องการ
function formatUsersResponse(users: User[], page: number = 1, limit: number = 10): GetUsersResponse {
  return {
    items: users,
    total: users.length,
    page: page,
    limit: limit
  };
}

// บริการจัดการข้อมูลผู้ใช้
const userService = {
  // ดึงข้อมูลผู้ใช้ทั้งหมด
  async getUsers(params: GetUsersParams = {}): Promise<GetUsersResponse> {
    try {
      // ใช้ค่าเริ่มต้นถ้าไม่มีการระบุ - Backend ใช้ page และ limit ตรงๆ
      const queryParams = {
        page: params.page || 1,
        limit: params.limit || 10,
        search: params.search,
        role_id: params.role_id,
        is_active: params.is_active,
        can_sign_in: params.can_sign_in || undefined,
      };
      
      const response = await ApiWithLogging.get<GetUsersResponse>('/users/', 
        { params: queryParams },
        { 
          service: 'userService',
          action: 'view_users_list',
          entityType: 'user'
        }
      );
      
      // ApiWithLogging.get จะ return data โดยตรง ไม่ใช่ response object
      const result = response;
      
      Logger.debug('Users list formatted successfully', { 
        totalUsers: result.total, 
        page: result.page, 
        limit: result.limit 
      });
      
      return result;
    } catch (error: any) {
      Logger.critical('Failed to fetch users list', error);
      throw error;
    }
  },

  // ดึงข้อมูลผู้ใช้ตาม id
  async getUser(id: number): Promise<User> {
    try {
      const response = await ApiWithLogging.get<User>(`/users/${id}`, 
        {},
        { 
          service: 'userService',
          action: 'view_user_detail',
          entityType: 'user',
          entityId: id
        }
      );
      
      Logger.debug('User detail loaded successfully', { userId: id, username: response.username });
      
      return response;
    } catch (error) {
      Logger.critical(`Failed to fetch user detail for ID: ${id}`, error);
      throw error;
    }
  },

  // ดึงข้อมูลผู้ใช้ปัจจุบัน
  async getCurrentUser(): Promise<User> {
    try {
      // ตรวจสอบว่ามี token หรือไม่
      const token = localStorage.getItem('access_token');
      console.log('Auth token exists for current user:', !!token);
      
      const response = await ApiWithLogging.get<User>('/users/me');
      return response;
    } catch (error) {
      console.error('Error in getCurrentUser service:', error);
      console.error('Detailed error:', error.response ? error.response.data : 'No response');
      console.error('Status code:', error.response ? error.response.status : 'Unknown');
      throw error;
    }
  },

  // สร้างผู้ใช้ใหม่
  async createUser(data: UserForm): Promise<User> {
    try {
      const response = await ApiWithLogging.post<User>('/users/', data);
      return response;
    } catch (error) {
      console.error('Error in createUser service:', error);
      console.error('Detailed error:', error.response ? error.response.data : 'No response');
      console.error('Status code:', error.response ? error.response.status : 'Unknown');
      throw error;
    }
  },

  // แก้ไขข้อมูลผู้ใช้
  async updateUser(id: number, data: Partial<UserForm>): Promise<User> {
    try {
      // จัดการ email field - ถ้ามี email แต่เป็นค่าว่าง ให้ส่ง null
      const updateData = {
        ...data,
        ...(data.email !== undefined && {
          email: data.email || null
        })
      };
      
      const response = await ApiWithLogging.put<User>(`/users/${id}`, updateData);
      return response;
    } catch (error) {
      console.error('Error in updateUser service:', error);
      console.error('Detailed error:', error.response ? error.response.data : 'No response');
      console.error('Status code:', error.response ? error.response.status : 'Unknown');
      throw error;
    }
  },

  // ลบผู้ใช้
  async deleteUser(id: number): Promise<User> {
    try {
      const response = await ApiWithLogging.delete<User>(`/users/${id}`);
      return response;
    } catch (error) {
      console.error('Error in deleteUser service:', error);
      throw error;
    }
  },

  // เปลี่ยนรหัสผ่าน
  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    try {
      const response = await ApiWithLogging.post<{ message: string }>('/users/me/password', {
        current_password: currentPassword,
        new_password: newPassword
      });
      return response;
    } catch (error) {
      console.error('Error in changePassword service:', error);
      throw error;
    }
  },

  // ตรวจสอบว่า username ซ้ำหรือไม่
  async checkUsernameExists(username: string, excludeId?: number): Promise<boolean> {
    try {
      const params: any = { search: username };
      const response = await ApiWithLogging.get<GetUsersResponse>('/users/', { params });

      // กรองเฉพาะที่ username ตรงกัน (case insensitive)
      const existingUsers = response.items.filter(user =>
        user.username.toLowerCase() === username.toLowerCase() &&
        (!excludeId || user.id !== excludeId)
      );

      return existingUsers.length > 0;
    } catch (error) {
      console.error('Error checking username:', error);
      return false;
    }
  },

  // ดึงรายชื่อผู้ดำเนินการสำหรับ dropdown ในหน้า Maintenance
  async getPerformers(): Promise<PerformerOption[]> {
    try {
      const response = await ApiWithLogging.get<PerformerOption[]>('/users/performers');
      return response;
    } catch (error) {
      console.error('Error in getPerformers service:', error);
      throw error;
    }
  },

  // ดึงรายชื่อ "พนักงานขาย" (เฉพาะ Role Sales) สำหรับ dropdown พนักงานขายที่ดูแลในฟอร์มบริษัท
  async getSalesPersons(): Promise<PerformerOption[]> {
    try {
      const response = await ApiWithLogging.get<PerformerOption[]>('/users/sales-persons');
      return response;
    } catch (error) {
      console.error('Error in getSalesPersons service:', error);
      throw error;
    }
  }
};

export default userService;